import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Auth,
  UserCredential,
  User,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut,
  updatePhoneNumber,
  PhoneAuthCredential,
  signInWithCustomToken,
} from "firebase/auth";
import { useDispatch, useSelector } from "react-redux";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  ArchetypeDiscovery,
  DimensionEvent,
  DiscoveredStory,
  ElementDiscovery,
  PatternDiscovery,
  DiscoveredPersonalityAnalysis,
  DiscoverablePersonalityAnalysis,
  FriendRequest,
  LlmRequest,
  DiscoveredSnippet,
  ConnectionLlmRequest,
  PublicProfile,
  FeedItemNew,
  LatestResult,
  AppInfo,
  PeerAssessmentEvent,
  AggregatePeerScore,
  ChatRoom,
  DiscoverableCompatibilitySnippet,
  PeriodicQuiz,
  PeriodicQuizResult,
  InsightDelivery,
  StoryDelivery,
  UserPoll,
  PollResponse,
  CommunityQuestion,
  DimensionSummary,
  ScheduledAssessment,
  TraitAssessmentResult,
  WeeklyTwoByTwo,
  DeliveredWeeklyTwoByTwo,
  WeeklyTierList,
  DeliveredWeeklyTierList,
  WeeklyVenn,
  DeliveredWeeklyVenn,
} from "@dimensional-engineering/dimensional-models";
import { getDownloadURL, ref } from "firebase/storage";

import { auth, db, firebaseStorage } from "@/_firebase/firebaseConfig";
import {
  resetState,
  setArchetypeDiscoveries,
  setCloseFriendUidList,
  setCognitiveFinished,
  setDeepFriendUidList,
  setDimensionData,
  setDiscoveredStories,
  setElementDiscoveries,
  setFriendRequestRecievedUidsList,
  setFriendRequestsRecieved,
  setFriendRequestsSentUidList,
  setFriendUidList,
  setImageURL,
  setIsCloseFriendRequestsSentUidList,
  setIsDeepFriendRequestsSentUidList,
  setIsPremium,
  setMemberProfile,
  setNutshellLlmRequests,
  setPatternDiscoveries,
  setPrimaryFinished,
  setValuesFinished,
  setPublicProfileData,
  setInviteLevel,
  setPeerFeedbackLevel,
  setAggregatePeerScore,
  setDiscoverableStories,
  setDimensionSummaries,
  setUserIsInitialized,
} from "@/redux/slices/userSlice";
import {
  setHasLoveReportLlmData,
  setHasCareerReportLlmData,
  setHasTherapistReportLlmData,
  setTherapistReportLlmRequest,
  setLoveReportLlmRequest,
  setCareerReportLlmRequest,
} from "@/redux/slices/reportSlice";
import {
  filterSharedPosts,
  gerDeliveredDailyStory,
  getAllStories,
  getCommentThread,
  getDefaultProfilePhoto,
  getDeliveredDailyInsight,
  handleConnections,
  handlePeerAssessmentRecommendationSort,
  isExpired,
  getAllCurrentChatbotConversations,
} from "./utils";
import { Mixpanel } from "@/helpers/mixpanel";
import {
  FriendsPublicProfile,
  PrivateMemberProfile,
  ReadOnlyPrivateProfile,
  RevenueCatSubscription,
  SharedPostWithCommentThread,
} from "@/models/sharedModels";
import {
  resetConnectionState,
  setConnectionPublicProfiles,
} from "@/redux/slices/connectionSlice";
import Alert from "@/components/shared/Alerts/Alert";
import axios from "axios";
import {
  setSnippets,
  setSnippetsAllLlmRequests,
  setSnippetsLatestManualLlmRequest,
} from "@/redux/slices/snippetsSlice";
import {
  setDiscoverablePersonalAnalysis,
  setDiscoveredPersonalAnalysis,
} from "@/redux/slices/personalityAnalysisSlice";
import {
  getFeedBackLevelFromNumOfPeerAssessments,
  getRewardsLevelFromNumOfInvites,
} from "@/components/appHome/utils";
import { useRouter } from "next/router";
import { useAlertContext } from "./AlertContext";
import {
  paginateFeed,
  setFeed,
  updateLastNotification,
} from "@/redux/slices/feedSlice";
import { RootState } from "@/redux/store";
import {
  paginateLatestResult,
  setLatestResults,
  setLatestResultsLoaded,
  updateLastResultPulled,
} from "@/redux/slices/latestResultsSlice";
import { setAppInfo } from "@/redux/slices/appInfoSlice";
import {
  setCtaRecommendationList,
  setPeerAssessmentEvents,
} from "@/redux/slices/peerAssessmentSlice";
import { useGetFCMTokenHook } from "@/helpers/useGetFCMTokenHook";
import {
  setDailyStory,
  setDailyStoryCommentThread,
  setDailyStoryLoaded,
  setStoryDelivery,
} from "@/redux/slices/dailyStorySlice";
import useScreenshotHandler from "@/helpers/useScreenshotHandler";
import { setChatRooms } from "@/redux/slices/messagingSlice";
import { setDiscoverableCompatibilitySnippets } from "@/redux/slices/compatibilitySnippetsSlice";
import {
  setAvailableQuiz,
  setPeriodicQuizCommentThread,
  setPeriodicQuizLoaded,
  setPeriodicQuizResults,
} from "@/redux/slices/periodicQuizSlice";
import { callFirebaseFunctions } from "@/_firebase/callFirebaseFunctions";
import {
  setCommentThread,
  setDailyInsightLoaded,
  setDeliveredDailyInsight,
  setInsightDelivery,
} from "@/redux/slices/dailyInsightsSlice";
// import {
//   setCommentThreadUserPoll,
//   setUserPoll,
//   setUserPollLoaded,
//   setUserPollResponse,
// } from "@/redux/slices/userPoll";
import {
  setIsShareTabClicked,
  setSharedPosts,
  setSharedPostsLoaded,
} from "@/redux/slices/sharedPostSlice";
import { getUserPollResponse } from "@/components/shared/LoadingModal/utils";
import { useModalContext } from "./ModalContext";
// import {
//   setCommentThreadCommunityQuestion,
//   setCommunityQuestion,
//   setCommunityQuestionLoaded,
// } from "@/redux/slices/communityQuestionSlice";
import {
  setConversations,
  setCurrentConversation,
  setReadOnlyPrivateProfile,
} from "@/redux/slices/chatbotSlice";

import elementsJsonData from "../assets/traitInfo/elements.json";
import {
  hasCompletedRequiredDimensions,
  listUserCompletedDimensionsForGating,
} from "@/components/authenticatedProfile/tabs/AuthCompareTab/PeerStories/utils";
import { shortFormToLongFormDimensionSlugMap } from "@/assets/traitInfo/dimensionData";
import {
  setScheduledAssessments,
  setScheduleIdentitfiers,
  setTraitAssessmentResults,
} from "@/redux/slices/scheduledAssessmentSlice";
import {
  setDeliveredWeeklyTwoByTwo,
  setLatestWeeklyTwoByTwo,
  setWeeklyTwoByTwoLoaded,
} from "@/redux/slices/weeklyTwoByTwoSlice";
import {
  setDeliveredWeeklyTierList,
  setLatestWeeklyTierList,
  setWeeklyTierListLoaded,
} from "@/redux/slices/weeklyTierListSlice";
import {
  setDeliveredWeeklyVenn,
  setLatestWeeklyVenn,
  setWeeklyVennLoaded,
} from "@/redux/slices/weeklyVennSlice";

const appleProvider = new OAuthProvider("apple.com");
const googleProvider = new GoogleAuthProvider();

export interface AuthProviderProps {
  children?: ReactNode;
}

export interface AuthContextModel {
  auth: Auth;
  user: User | null | undefined;
  signInWithApple: () => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  signOutOfAccount: () => Promise<unknown>;
  signInUsingCustomToken: (token: string) => Promise<UserCredential>;
  getUser: () => User | null | undefined;
  updateUserPhoneNumber: (
    phoneCredential: PhoneAuthCredential
  ) => Promise<void> | undefined;
  paginateFeedItems: () => void;
  paginateLatestResults: () => void;
}

export const AuthContext = createContext<AuthContextModel>(
  {} as AuthContextModel
);

///provider

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  useGetFCMTokenHook();
  useScreenshotHandler();

  const userState = useSelector((state: RootState) => state.user);
  const quizState = useSelector((state: RootState) => state.periodicQuiz);
  const dailyStoryState = useSelector((state: RootState) => state.dailyStory);
  const dailyInsightState = useSelector(
    (state: RootState) => state.dailyInsight
  );
  const pollState = useSelector((state: RootState) => state.userPoll);

  const lastNotification = useSelector(
    (state: RootState) => state.feed.lastNotification
  );
  const lastResultPulled = useSelector(
    (state: RootState) => state.latestResults.lastResultPulled
  );

  const peerAssessmentState = useSelector(
    (state: RootState) => state.peerAssessment
  );

  const [user, setUser] = useState<User | null | undefined>();
  const dispatch = useDispatch();
  const router = useRouter();
  const { setAlertComponent, removeAlert } = useAlertContext();
  const { setModalComponent } = useModalContext();

  function signInWithGoogle(): Promise<UserCredential> {
    return signInWithPopup(auth, googleProvider);
  }

  function signInWithApple(): Promise<UserCredential> {
    return signInWithPopup(auth, appleProvider);
  }

  function signInUsingCustomToken(token: string) {
    return signInWithCustomToken(auth, token);
  }

  function signOutOfAccount() {
    dispatch(resetState());
    dispatch(resetConnectionState());

    return signOut(auth);
  }

  function getUser() {
    return user;
  }

  function updateUserPhoneNumber(phoneCredential: PhoneAuthCredential) {
    if (user) return updatePhoneNumber(user, phoneCredential);
  }

  useEffect(() => {
    ///make last signin update on refrsh token change
    const unsubsrcibe = auth.onAuthStateChanged((user) => {
      setUser(user);
      if (user != null) {
        if (window && window.Android) {
          localStorage.setItem("uid", user.uid);
        }
      }
    });
    return () => {
      unsubsrcibe;
    };
  }, []);

  useEffect(() => {
    if (userState.publicProfileData?.ownerUUID) {
      dispatch(setUserIsInitialized(true));
    }
  }, [
    userState.publicProfileData?.ownerUUID,
    dailyStoryState.dailyStoryLoaded,
    quizState.periodicQuizLoaded,
    dailyInsightState.dailyInsightLoaded,
  ]);

  useEffect(() => {
    if (userState.isUserInitialized && window && window.Android) {
      console.log("Sent onUserReady");
      window.Android.onUserReady();
    }
  }, [userState.isUserInitialized]);

  useEffect(() => {
    const unsubscribeAppInfo = onSnapshot(
      doc(db, "appInfo/dimensionalIOS"),
      (doc) => {
        if (doc.exists()) {
          const appInfo = doc.data() as AppInfo;
          dispatch(setAppInfo(appInfo));
        }
      },
      (err) => {
        console.log(err);
      }
    );

    const unsubscribeMemberProfile = onSnapshot(
      doc(db, "members", `${user?.uid}`),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as PrivateMemberProfile;
          Mixpanel?.people.update(
            {
              "Chatbot | total_tokens_used": data.lifetimeGPTTokensConsumed,
            },
            user?.uid
          );
          dispatch(setMemberProfile(data));

          const userRewardLevel = getRewardsLevelFromNumOfInvites(
            data?.recommendeeUids?.length
          );
          dispatch(setInviteLevel(userRewardLevel));

          const userPeerFeedbackLevel =
            getFeedBackLevelFromNumOfPeerAssessments(data.peerAssesseeUids);
          dispatch(setPeerFeedbackLevel(userPeerFeedbackLevel));

          Mixpanel?.people.update(
            {
              "successful invites": data?.recommendeeUids?.length,
            },
            user?.uid
          );
          Mixpanel?.people.update(
            {
              "peer assessments received": data.peerAssesseeUids?.length,
            },
            user?.uid
          );
        }
      }
    );

    const unsubscribePublicProfile = onSnapshot(
      doc(db, "publicProfiles", `${user?.uid}`),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as PublicProfile;
          dispatch(setPublicProfileData(data));
        }
      }
    );

    const dimensionQuery = query(
      collection(db, "members", `${user?.uid}`, "dimensionEvents")
    );

    const unsubscribeDimensionEvents = onSnapshot(
      dimensionQuery,
      (querySnapshot) => {
        const initiated: DimensionEvent[] = [];
        querySnapshot.forEach((doc) => {
          let dimEvent = doc.data() as DimensionEvent;
          initiated.push(dimEvent);
        });

        const finishedSlugs = listUserCompletedDimensionsForGating(initiated);

        Mixpanel?.people.update(
          { "Dimensions Completed": finishedSlugs },
          user?.uid
        );

        if (finishedSlugs.includes("personality")) {
          dispatch(setPrimaryFinished(true));
        } else {
          dispatch(setPrimaryFinished(true));
        }

        if (finishedSlugs.includes("personality-II")) {
          dispatch(setCognitiveFinished(true));
        } else {
          dispatch(setCognitiveFinished(true));
        }

        if (finishedSlugs.includes("values")) {
          dispatch(setValuesFinished(true));
        } else {
          dispatch(setValuesFinished(true));
        }

        dispatch(setDimensionData(initiated));
      },
      (err) => {
        console.log("Error on dimension events listener: ", err);
      }
    );

    ///scales
    //members/<uid>/elementDiscoveries
    const scalesQuery = query(
      collection(db, "members", `${user?.uid}`, "elementDiscoveries"),
      where("scaleScore", "!=", null)
    );
    const unsubscribeScales = onSnapshot(
      scalesQuery,
      (querySnapshot) => {
        const elements: ElementDiscovery[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as ElementDiscovery;
          const found = elementsJsonData.find((e) => e.slug === data.scaleSlug);
          if (
            found &&
            found.isDiscoverable === true &&
            !data.scaleScore?.isStale
          ) {
            elements.push(data);
          }
        });
        dispatch(setElementDiscoveries(elements));
        // querySnapshot.docChanges().forEach((doc) => {
        //   if (mod10(user?.uid)) {
        //     Mixpanel?.track("Element Discovered", {},user?.uid);
        //   }
        // });
      },
      (err) => {
        console.log("Error on Element Discovery: ", err);
      }
    );

    ///patterns
    //members/<uid>/patternDiscoveries
    const patternsQuery = query(
      collection(db, "members", `${user?.uid}`, "patternDiscoveries")
    );

    const unsubscribePatterns = onSnapshot(
      patternsQuery,
      (querySnapshot) => {
        const patterns: PatternDiscovery[] = [];
        querySnapshot.forEach((doc) => {
          let pattern = doc.data() as PatternDiscovery;
          if (!pattern.isStale) {
            patterns.push(pattern);
          }
        });
        dispatch(setPatternDiscoveries(patterns));
      },
      (err) => {
        console.log("Error on Pattern Discovery: ", err);
      }
    );

    const archetypesQuery = query(
      collection(db, "members", `${user?.uid}`, "archetypeDiscoveries")
    );

    const unsubscribeArchetypes = onSnapshot(
      archetypesQuery,
      (querySnapshot) => {
        let archetypes: ArchetypeDiscovery[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as ArchetypeDiscovery;
          if (data.archetype.archetypeGroupSlug !== "values") {
            archetypes.push(data);
          }
        });
        dispatch(setArchetypeDiscoveries(archetypes));
      },
      (err) => {
        console.log("Error on Archetype Discovery: ", err);
      }
    );

    const discoveredDoc = query(
      collection(db, "members", `${user?.uid}`, "discoveredStories"),
      where("storyType", "==", "listicle")
    );
    const unsubscribeDiscoveredStories = onSnapshot(
      discoveredDoc,
      (querySnapshot) => {
        let stories: DiscoveredStory[] = [];
        querySnapshot.forEach((doc) => {
          stories.push(doc.data() as DiscoveredStory);
        });
        dispatch(setDiscoveredStories(stories));
      },
      (err) => {
        console.log("Error On Discovered Stories Listener: ", err);
      }
    );

    const discoverableAnalysisQuery = query(
      collection(db, "discoverablePersonalityAnalyses")
    );

    const unsubscribeDiscoverableAnalysis = onSnapshot(
      discoverableAnalysisQuery,
      (querySnapshot) => {
        let analysises: DiscoverablePersonalityAnalysis[] = [];
        querySnapshot.forEach((doc) => {
          analysises.push(doc.data() as DiscoverablePersonalityAnalysis);
        });
        dispatch(setDiscoverablePersonalAnalysis(analysises));
      },
      (err) => {
        console.log(
          "Error On Discoverable Personality Analysis Listener: ",
          err
        );
      }
    );

    const discoverableCompatibilitySnippetsDoc = collection(
      db,
      "discoverableCompatibilitySnippets"
    );

    const unsubscribeDiscoverableCompatiblitySNippets = onSnapshot(
      discoverableCompatibilitySnippetsDoc,
      (querySnapshot) => {
        let array: DiscoverableCompatibilitySnippet[] = [];
        querySnapshot.forEach((doc) => {
          array.push(doc.data() as DiscoverableCompatibilitySnippet);
        });
        if (array.length) {
          dispatch(setDiscoverableCompatibilitySnippets(array));
        }
      }
    );

    function getIsPremium() {
      axios
        .get(`https://api.revenuecat.com/v1/subscribers/${user?.uid}`, {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_REVENUE_CAT}`,
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          const data = res.data as RevenueCatSubscription;
          if (data.subscriber.entitlements.premium) {
            const expired = isExpired(
              data.subscriber.entitlements.premium.expires_date
            );
            if (!expired) {
              dispatch(setIsPremium(true));
            } else {
              dispatch(setIsPremium(false));
            }
          } else {
            dispatch(setIsPremium(false));
          }
        })
        .catch((err) => {
          console.log("error from revenue cat", err);
        });
    }
    ///have to use conditional for query due to firebase error on dot notation

    let recievedfreindRequestDoc;

    if (user?.uid !== undefined) {
      recievedfreindRequestDoc = query(
        collection(db, "friendRequests"),
        where("receiver.ownerUUID", "==", user?.uid),
        where("status", "==", "sent")
      );
    } else {
      recievedfreindRequestDoc = query(
        collection(db, "friendRequests"),
        where("status", "==", "sent")
      );
    }

    const unsubscribeRecievedFriendRequestDoc = onSnapshot(
      recievedfreindRequestDoc,
      (querySnapshot) => {
        let requests: FriendRequest[] = [];
        let requestUids: string[] = [];
        querySnapshot.forEach((doc) => {
          requests.push(doc.data() as FriendRequest);
          requestUids.push(doc.data().sender.ownerUUID);
        });
        dispatch(setFriendRequestsRecieved(requests));
        dispatch(setFriendRequestRecievedUidsList(requestUids));
      },
      (err) => {
        console.log("Error On Friend Request Listener: ", err);
      }
    );

    let sentFriendRequestDoc;

    if (user?.uid !== undefined) {
      sentFriendRequestDoc = query(
        collection(db, "friendRequests"),
        where("sender.ownerUUID", "==", user?.uid),
        where("status", "==", "sent")
      );
    } else {
      sentFriendRequestDoc = query(
        collection(db, "friendRequests"),
        where("status", "==", "sent")
      );
    }

    const unsubscribeSentFriendRequestDoc = onSnapshot(
      sentFriendRequestDoc,
      (querySnapshot) => {
        let requests: string[] = [];
        let isCloseList: string[] = [];
        let isDeepList: string[] = [];

        querySnapshot.forEach((doc) => {
          const uid = doc.data().receiver.ownerUUID;
          requests.push(uid);
          if (doc.data().isClose === true && !doc.data().isDeep) {
            isCloseList.push(doc.data().receiver.ownerUUID);
          }
          if (doc.data().isDeep === true) {
            isDeepList.push(doc.data().receiver.ownerUUID);
          }
        });
        dispatch(setFriendRequestsSentUidList(requests));
        dispatch(setIsCloseFriendRequestsSentUidList(isCloseList));
        dispatch(setIsDeepFriendRequestsSentUidList(isDeepList));
      },
      (err) => {
        console.log("Error On Sent Frtiend Request Listener: ", err);
      }
    );

    /////

    const accessMapRef = doc(
      db,
      "members",
      `${user?.uid}`,
      "accessMaps",
      "friends"
    );

    const unsubscribeAccessMapp = onSnapshot(
      accessMapRef,
      (querySnapshot) => {
        if (querySnapshot.exists()) {
          let closeList: string[] = [];
          let deepList: string[] = [];
          for (const key in querySnapshot.data().accessMap) {
            if (querySnapshot.data().accessMap.hasOwnProperty(key)) {
              const item = querySnapshot.data().accessMap[key];
              if (item.isClose === true) {
                closeList.push(key);
              }
              if (item.isDeep === true) {
                deepList.push(key);
              }
            }
          }
          dispatch(
            setFriendUidList(
              Object.keys(querySnapshot.data().accessMap as string[])
            )
          );
          dispatch(setCloseFriendUidList(closeList));
          dispatch(setDeepFriendUidList(deepList));
          if (user?.uid) {
            handleConnections(user?.uid).then((res) => {
              const friendsList = res as FriendsPublicProfile[];
              dispatch(setConnectionPublicProfiles(friendsList));
              Mixpanel?.people.update({ friendCount: friendsList.length });
            });
          }
        }
      },
      (err) => {
        console.log("Error On Access Map Listener: ", err);
      }
    );

    const nutshellRef = query(
      collection(db, `llmRequests/${user?.uid}/nutshell`)
    );

    const unsubscribeNutshellRequests = onSnapshot(
      nutshellRef,
      (querySnapshot) => {
        let allRequests: LlmRequest[] = [];
        querySnapshot.forEach((doc) => {
          allRequests.push(doc.data() as LlmRequest);
        });
        dispatch(setNutshellLlmRequests(allRequests));
      },
      (err) => {
        console.log("Error On Nutshell Listener: ", err);
      }
    );

    const snippetsAllLlmRef = query(
      collection(db, `llmRequests/${user?.uid}/snippets`),
      orderBy("createdOn", "desc"),
      limit(1)
    );
    const snippetsManualLlmRef = query(
      collection(db, `llmRequests/${user?.uid}/snippets`),
      orderBy("createdOn", "desc"),
      where("trigger", "==", "manualRetry"),
      limit(1)
    );

    const unsubscribeSnippetsAllRequests = onSnapshot(
      snippetsAllLlmRef,
      (querySnapshot) => {
        let allRequests: LlmRequest[] = [];
        querySnapshot.forEach((doc) => {
          allRequests.push(doc.data() as LlmRequest);
        });
        querySnapshot.docChanges().forEach((change) => {
          if (change.type === "modified") {
            let changedDoc = change.doc.data() as LlmRequest;
            if (changedDoc.status === "complete") {
              if (changedDoc.contentIdentifier?.contentSlug) {
                setAlertComponent(
                  <Alert
                    elementName="Alert"
                    type="success"
                    message="Snippets generated. See them on your Results tab."
                    buttonText="View"
                    onButtonClick={() => {
                      if (changedDoc.contentIdentifier?.contentSlug) {
                        localStorage.setItem(
                          "snippetSlug",
                          changedDoc.contentIdentifier?.contentSlug
                        );
                      }
                      removeAlert();
                      router.push("/app/results");
                    }}
                  />,
                  20000
                );
              } else {
                setAlertComponent(
                  <Alert
                    elementName="Alert"
                    type="success"
                    message="Snippets generated. See them on your Results tab."
                  />,
                  5000
                );
              }
            }
          }
        });
        dispatch(setSnippetsAllLlmRequests(allRequests));
      },
      (err) => {
        console.log("Error On Snippets Listener: ", err);
      }
    );

    const unsubscribeLatestSnippetsManualLlmRequest = onSnapshot(
      snippetsManualLlmRef,
      (querySnapshot) => {
        let allRequests: LlmRequest[] = [];
        querySnapshot.forEach((doc) => {
          allRequests.push(doc.data() as LlmRequest);
        });
        dispatch(setSnippetsLatestManualLlmRequest(allRequests[0]));
      },
      (err) => {
        console.log("Error On Manual LLM Requests Listener: ", err);
      }
    );

    const loveReportRef = query(
      collection(db, `members/${user?.uid}/loveReportLlmData`),
      orderBy("createdOn", "desc"),
      limit(1)
    );
    const unsubscribeLoveReportLlmData = onSnapshot(
      loveReportRef,
      (querySnapShot) => {
        const hasLoveReport = !querySnapShot?.empty;
        dispatch(setHasLoveReportLlmData(hasLoveReport));
      },
      (err) => {
        console.log("Error On Love Report Listener: ", err);
      }
    );

    const loveReportLlmRequest = query(
      collection(db, `llmRequests/${user?.uid}/loveReport`),
      where("trigger", "==", "manualRetry"),
      where("status", "==", "processing"),
      limit(1)
    );

    const unsubscribeLoveReportLlmRequest = onSnapshot(
      loveReportLlmRequest,
      (querySnapShot) => {
        let allRequests: LlmRequest[] = [];
        querySnapShot.forEach((doc) => {
          allRequests.push(doc.data() as LlmRequest);
        });
        dispatch(setLoveReportLlmRequest(allRequests[0]));
      },
      (err) => {
        console.log("Error On Lover Report Request Listener: ", err);
      }
    );

    const careerReportRef = query(
      collection(db, `members/${user?.uid}/careerReportLlmData`),
      orderBy("createdOn", "desc"),
      limit(1)
    );
    const unsubscribeCareerReportLlmData = onSnapshot(
      careerReportRef,
      (querySnapShot) => {
        const hasCareerReport = !querySnapShot?.empty;
        dispatch(setHasCareerReportLlmData(hasCareerReport));
      },
      (err) => {
        console.log("Error On Career Report Listener: ", err);
      }
    );

    const careerReportLlmRequest = query(
      collection(db, `llmRequests/${user?.uid}/careerReport`),
      where("trigger", "==", "manualRetry"),
      where("status", "==", "processing"),
      limit(1)
    );

    const unsubscribeCareerReportLlmRequest = onSnapshot(
      careerReportLlmRequest,
      (querySnapShot) => {
        let allRequests: LlmRequest[] = [];
        querySnapShot.forEach((doc) => {
          allRequests.push(doc.data() as LlmRequest);
        });
        dispatch(setCareerReportLlmRequest(allRequests[0]));
      },
      (err) => {
        console.log("Error On Career Report Request Listener: ", err);
      }
    );

    const therapistReportRef = query(
      collection(db, `members/${user?.uid}/therapistReportLlmData`),
      orderBy("createdOn", "desc"),
      limit(1)
    );
    const unsubscribeTherapistReportLlmData = onSnapshot(
      therapistReportRef,
      (querySnapShot) => {
        const hasTherapistReport = !querySnapShot?.empty;
        dispatch(setHasTherapistReportLlmData(hasTherapistReport));
      },
      (err) => {
        console.log("Error On Therapist Report Listener: ", err);
      }
    );

    const therapistReportLlmRequest = query(
      collection(db, `llmRequests/${user?.uid}/therapistReport`),
      where("trigger", "==", "manualRetry"),
      where("status", "==", "processing"),
      limit(1)
    );

    const unsubscribeTherapistReportLlmRequest = onSnapshot(
      therapistReportLlmRequest,
      (querySnapShot) => {
        let allRequests: LlmRequest[] = [];
        querySnapShot.forEach((doc) => {
          allRequests.push(doc.data() as LlmRequest);
        });
        dispatch(setTherapistReportLlmRequest(allRequests[0]));
      },
      (err) => {
        console.log("Error On Therapist Report Request Listener: ", err);
      }
    );

    const feedQueryFirst = query(
      collection(db, `members/${user?.uid}/feed`),
      orderBy("createdOn", "desc"),
      limit(10)
    );

    const unsubscribeInitialFeed = onSnapshot(
      feedQueryFirst,
      (querySnapShot) => {
        let allFeedItems: FeedItemNew[] = [];
        querySnapShot.forEach((doc) => {
          allFeedItems.push(doc.data() as FeedItemNew);
        });
        dispatch(
          updateLastNotification(
            querySnapShot.docs[querySnapShot.docs.length - 1]
          )
        );
        dispatch(setFeed(allFeedItems));
      },
      (err) => {
        console.log("Error On Feed Listener: ", err);
      }
    );

    const latestResultsInitialQuery = query(
      collection(db, `members/${user?.uid}/latestResults`),
      orderBy("createdOn", "desc"),
      limit(20)
    );

    const unsubscribeLatestResults = onSnapshot(
      latestResultsInitialQuery,
      (querySnapShot) => {
        dispatch(setLatestResultsLoaded(true));
        let allResults: LatestResult[] = [];
        querySnapShot.forEach((doc) => {
          allResults.push(doc.data() as LatestResult);
        });
        dispatch(
          updateLastResultPulled(
            querySnapShot.docs[querySnapShot.docs.length - 1]
          )
        );
        dispatch(setLatestResults(allResults));
      },
      (err) => {
        console.log("Error On Latest Results Listener: ", err);
      }
    );

    const peerAssessmentEventsRef = collection(
      db,
      "members",
      `${user?.uid}`,
      "peerAssessmentEvents"
    );

    const unsubscribePeerAssessments = onSnapshot(
      peerAssessmentEventsRef,
      (querySnapShot) => {
        let allAssessments: PeerAssessmentEvent[] = [];
        querySnapShot.forEach((doc) => {
          allAssessments.push(doc.data() as PeerAssessmentEvent);
        });
        dispatch(setPeerAssessmentEvents(allAssessments));
      },
      (err) => {
        console.log("Error On Peer Assessments Listener: ", err);
      }
    );

    const dailyStoryRef = query(
      collection(db, "/storyDeliveries"),
      orderBy("deliveryDate", "desc"),
      limit(1)
    );

    const unsubscribeDailyStory = onSnapshot(
      dailyStoryRef,
      (querySnapshot) => {
        querySnapshot.forEach(async (result) => {
          const data = result.data() as StoryDelivery;
          dispatch(setStoryDelivery(data));
          await gerDeliveredDailyStory(user?.uid, data.id)
            .then(async (res) => {
              dispatch(setDailyStoryLoaded(true));
              dispatch(setDailyStory(res));
              if (res?.assignmentRuleset.commentThreadId) {
                await getCommentThread(
                  res.assignmentRuleset.commentThreadId
                ).then((commentResult) => {
                  if (commentResult) {
                    dispatch(setDailyStoryCommentThread(commentResult));
                  }
                });
              }
            })
            .catch((err) => {
              dispatch(setDailyStoryLoaded(true));
            });
        });
      },
      (err) => {
        console.log("Error on daily stories listener: ", err);
      }
    );

    const aggregatePeerScoreRef = collection(
      db,
      "members",
      `${user?.uid}`,
      "aggregatePeerScores"
    );

    const unsubscribeAggregatePeerScore = onSnapshot(
      aggregatePeerScoreRef,
      (querySnapshot) => {
        const allPeerScores: AggregatePeerScore[] = [];
        querySnapshot.forEach((doc) => {
          allPeerScores.push(doc.data() as AggregatePeerScore);
        });
        dispatch(setAggregatePeerScore(allPeerScores));
      },
      (err) => {
        console.log("Peer score listener error: ", err);
      }
    );

    const messagingQuery = query(
      collection(db, "chatRooms"),
      where("participantUids", "array-contains", user?.uid ?? "null")
    );

    const unsubscribeMessaging = onSnapshot(messagingQuery, (querySnapshot) => {
      const chatRooms: ChatRoom[] = [];
      querySnapshot.forEach((doc) => {
        chatRooms.push(doc.data() as ChatRoom);
      });
      querySnapshot.docChanges().forEach((doc) => {
        if (doc.type === "modified") {
          const data = doc.doc.data() as ChatRoom;
          if (user?.uid && !data.latestMessage?.readBy?.includes(user.uid)) {
            if (window.Android) {
              window.Android.onHaptic();
            }
            setAlertComponent(
              <Alert
                message={`New message received`}
                type="chat"
                elementName="Alert"
              />,
              3000
            );
          }
        }
      });
      dispatch(setChatRooms(chatRooms));
    });

    const availableQuizRef = query(
      collection(db, "/periodicQuizzes"),
      orderBy("deliveredOn", "desc"),
      limit(1)
    );

    const unsubscribeAvailableQuiz = onSnapshot(
      availableQuizRef,
      (querySnapshot) => {
        querySnapshot.forEach(async (quiz) => {
          const data = quiz.data() as PeriodicQuiz;
          dispatch(setAvailableQuiz(data));
          if (data.commentThreadId) {
            await getCommentThread(data.commentThreadId).then(
              (commentResult) => {
                if (commentResult) {
                  dispatch(setPeriodicQuizCommentThread(commentResult));
                }
              }
            );
          }
        });
      }
    );

    const quizResults = collection(
      db,
      `/members/${user?.uid}/periodicQuizResults`
    );

    const unsubscribePeriodicQuizResults = onSnapshot(
      quizResults,
      (querySnapshot) => {
        dispatch(setPeriodicQuizLoaded(true));
        let results: PeriodicQuizResult[] = [];
        querySnapshot.forEach((result) => {
          results.push(result.data() as PeriodicQuizResult);
        });
        dispatch(setPeriodicQuizResults(results));
      }
    );

    const insightDeliveryRef = query(
      collection(db, "/insightDeliveries"),
      orderBy("deliveryDate", "desc"),
      limit(1)
    );

    const unsubscribeInsightDelivery = onSnapshot(
      insightDeliveryRef,
      (querySnapshot) => {
        querySnapshot.forEach(async (result) => {
          const data = result.data() as InsightDelivery;
          dispatch(setInsightDelivery(data));
          await getDeliveredDailyInsight(user?.uid, data.id)
            .then(async (res) => {
              dispatch(setDailyInsightLoaded(true));
              dispatch(setDeliveredDailyInsight(res));
              if (res?.assignmentRuleset.commentThreadId) {
                await getCommentThread(
                  res.assignmentRuleset.commentThreadId
                ).then((commentResult) => {
                  if (commentResult) {
                    dispatch(setCommentThread(commentResult));
                  }
                });
              }
            })
            .catch((err) => {
              dispatch(setDailyInsightLoaded(true));
            });
        });
      },
      (err) => {
        console.log("Error on daily insights listener: ", err);
      }
    );

    // const userPollRef = query(
    //   collection(db, "/userPolls"),
    //   orderBy("deliveryDate", "desc"),
    //   limit(1)
    // );

    // const unsubscribeDailyPolls = onSnapshot(userPollRef, (querySnapshot) => {
    //   querySnapshot.forEach(async (result) => {
    //     const data = result.data() as UserPoll;
    //     await getUserPollResponse(data.slug, user?.uid)
    //       .then((res) => {
    //         dispatch(setUserPollLoaded(true));
    //         dispatch(setUserPollResponse(res));
    //       })
    //       .catch(() => {
    //         dispatch(setUserPollLoaded(true));
    //       });
    //     dispatch(setUserPoll(data));
    //     if (data?.commentThreadId) {
    //       await getCommentThread(data?.commentThreadId).then(
    //         (commentResult) => {
    //           if (commentResult) {
    //             dispatch(setCommentThreadUserPoll(commentResult));
    //           }
    //         }
    //       );
    //     }
    //   });
    // });

    // const pollResponseRef = doc(
    //   db,
    //   `/members/${user?.uid}/pollResponses/${pollState.userPoll?.slug}`
    // );

    // const unsubscribePollResponse = onSnapshot(
    //   pollResponseRef,
    //   (querySnapshot) => {
    //     if (querySnapshot.exists()) {
    //       const data = querySnapshot.data() as PollResponse;
    //       dispatch(setUserPollResponse(data));
    //     } else {
    //       dispatch(setUserPollResponse(null));
    //     }
    //   }
    // );

    const sharedPostsRef = collection(db, `/sharedPosts`);

    const sharedPostQuery = query(
      sharedPostsRef,
      where(`audienceMap.${user?.uid}`, "==", true)
    );

    const unsubscribeSharedPosts = onSnapshot(
      sharedPostQuery,
      async (querySnapshot) => {
        const postPromises = querySnapshot.docs.map(async (document) => {
          let data = document.data() as SharedPostWithCommentThread;
          try {
            // Fetch the comment thread for the current document
            const commentThread = await getCommentThread(data.commentThreadId);
            data.commentThread = commentThread;
          } catch (err) {
            console.error(
              `Error fetching comment thread for ${data.commentThreadId}:`,
              err
            );
          }
          return data;
        });

        try {
          const posts: SharedPostWithCommentThread[] = await Promise.all(
            postPromises
          );
          if (user?.uid) {
            const myPosts = posts.filter((p) => p.authorUid === user?.uid);
            Mixpanel?.people.update(
              {
                "Post count": myPosts.length,
              },
              user.uid
            );
          }
          dispatch(setSharedPosts(filterSharedPosts(posts)));
          dispatch(setSharedPostsLoaded(true));
        } catch (err) {
          console.error("Error resolving post promises:", err);
        }
        querySnapshot.docChanges().forEach((d) => {
          if (d.type === "added") {
            dispatch(setIsShareTabClicked(false));
          }
        });
      }
    );

    // const communityQuestionRef = collection(db, `/communityQuestions`);
    // const communityQuestionQuery = query(
    //   communityQuestionRef,
    //   orderBy("deliveryDate", "desc"),
    //   limit(1)
    // );

    // const unsubscribeCommunityQuestions = onSnapshot(
    //   communityQuestionQuery,
    //   (querySnapshot) => {
    //     dispatch(setCommunityQuestionLoaded(true));
    //     querySnapshot.forEach(async (d) => {
    //       const data = d.data() as CommunityQuestion;
    //       dispatch(setCommunityQuestion(data));
    //       if (data.commentThreadId) {
    //         await getCommentThread(data?.commentThreadId).then((res) => {
    //           if (res) {
    //             dispatch(setCommentThreadCommunityQuestion(res));
    //           }
    //         });
    //       }
    //     });
    //   }
    // );

    const readOnlyProfileRef = doc(db, `/readOnlyPrivateProfiles/${user?.uid}`);

    const unsubscribeReadOnlyProfile = onSnapshot(
      readOnlyProfileRef,
      (querySnapshot) => {
        if (querySnapshot.exists()) {
          const data = querySnapshot.data() as ReadOnlyPrivateProfile | null;
          Mixpanel?.people.update(
            {
              "Chatbot | total user messages sent":
                data?.lifetimeGPTMessagesSent,
            },
            user?.uid
          );
          dispatch(setReadOnlyPrivateProfile(data));
        }
      }
    );

    const thirtyDaysAgo = Timestamp.fromDate(
      new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );
    const scheduledAssessmentsRef = collection(db, "/scheduledAssessments");
    const scheduledAssessmentsQuery = query(
      scheduledAssessmentsRef,
      where("lastOccurrence", ">=", thirtyDaysAgo)
    );

    const unsubscribeScheduledAssessmentsRef = onSnapshot(
      scheduledAssessmentsQuery,
      (querySnapshot) => {
        const assessments: ScheduledAssessment[] = [];
        querySnapshot.forEach((d) => {
          const data = d.data() as ScheduledAssessment;
          assessments.push(data);
        });
        dispatch(setScheduledAssessments(assessments));
      }
    );

    // Latest released weekly 2x2 chart (root collection). The backend's
    // scheduled fan-out marks `releasedOn` when releasing each week's chart.
    const latestWeeklyTwoByTwoQuery = query(
      collection(db, "/weeklyTwoByTwos"),
      where("releasedOn", ">", 0),
      orderBy("releasedOn", "desc"),
      limit(1)
    );

    let unsubscribeDeliveredWeeklyTwoByTwo: (() => void) | null = null;

    const unsubscribeLatestWeeklyTwoByTwo = onSnapshot(
      latestWeeklyTwoByTwoQuery,
      (querySnapshot) => {
        const chart =
          querySnapshot.docs[0]?.data() as WeeklyTwoByTwo | undefined;

        console.log("[WeeklyTwoByTwo] latest snapshot received", {
          size: querySnapshot.size,
          empty: querySnapshot.empty,
          slug: chart?.slug,
          releasedOn: chart?.releasedOn,
          chart,
        });

        // Tear down any prior delivered listener — chart slug may have changed.
        if (unsubscribeDeliveredWeeklyTwoByTwo) {
          unsubscribeDeliveredWeeklyTwoByTwo();
          unsubscribeDeliveredWeeklyTwoByTwo = null;
        }

        if (!chart) {
          console.log(
            "[WeeklyTwoByTwo] no released chart found — clearing state"
          );
          dispatch(setLatestWeeklyTwoByTwo(null));
          dispatch(setDeliveredWeeklyTwoByTwo(null));
          dispatch(setWeeklyTwoByTwoLoaded(true));
          return;
        }

        dispatch(setLatestWeeklyTwoByTwo(chart));

        if (!user?.uid) {
          console.log(
            "[WeeklyTwoByTwo] no signed-in user — skipping delivered listener"
          );
          dispatch(setDeliveredWeeklyTwoByTwo(null));
          dispatch(setWeeklyTwoByTwoLoaded(true));
          return;
        }

        const deliveredPath = `/members/${user.uid}/weeklyTwoByTwos/${chart.slug}`;
        console.log(
          "[WeeklyTwoByTwo] subscribing to delivered doc",
          deliveredPath
        );
        const deliveredRef = doc(db, deliveredPath);

        unsubscribeDeliveredWeeklyTwoByTwo = onSnapshot(
          deliveredRef,
          (snap) => {
            const data = snap.exists()
              ? (snap.data() as DeliveredWeeklyTwoByTwo)
              : null;
            console.log("[WeeklyTwoByTwo] delivered snapshot received", {
              path: deliveredPath,
              exists: snap.exists(),
              data,
            });
            dispatch(setDeliveredWeeklyTwoByTwo(data));
            dispatch(setWeeklyTwoByTwoLoaded(true));
          },
          (err) => {
            console.log(
              "[WeeklyTwoByTwo] error on delivered listener:",
              deliveredPath,
              err
            );
            dispatch(setDeliveredWeeklyTwoByTwo(null));
            dispatch(setWeeklyTwoByTwoLoaded(true));
          }
        );
      },
      (err) => {
        console.log("[WeeklyTwoByTwo] error on latest listener:", err);
        dispatch(setLatestWeeklyTwoByTwo(null));
        dispatch(setDeliveredWeeklyTwoByTwo(null));
        dispatch(setWeeklyTwoByTwoLoaded(true));
      }
    );

    // Latest released weekly tier list (root collection). The backend's
    // scheduled fan-out marks `releasedOn` when releasing each week's tier list.
    const latestWeeklyTierListQuery = query(
      collection(db, "/weeklyTierLists"),
      where("releasedOn", ">", 0),
      orderBy("releasedOn", "desc"),
      limit(1)
    );

    let unsubscribeDeliveredWeeklyTierList: (() => void) | null = null;

    const unsubscribeLatestWeeklyTierList = onSnapshot(
      latestWeeklyTierListQuery,
      (querySnapshot) => {
        const tierList =
          querySnapshot.docs[0]?.data() as WeeklyTierList | undefined;

        console.log("[WeeklyTierList] latest snapshot received", {
          size: querySnapshot.size,
          empty: querySnapshot.empty,
          slug: tierList?.slug,
          releasedOn: tierList?.releasedOn,
          tierList,
        });

        if (unsubscribeDeliveredWeeklyTierList) {
          unsubscribeDeliveredWeeklyTierList();
          unsubscribeDeliveredWeeklyTierList = null;
        }

        if (!tierList) {
          console.log(
            "[WeeklyTierList] no released tier list found — clearing state"
          );
          dispatch(setLatestWeeklyTierList(null));
          dispatch(setDeliveredWeeklyTierList(null));
          dispatch(setWeeklyTierListLoaded(true));
          return;
        }

        dispatch(setLatestWeeklyTierList(tierList));

        if (!user?.uid) {
          console.log(
            "[WeeklyTierList] no signed-in user — skipping delivered listener"
          );
          dispatch(setDeliveredWeeklyTierList(null));
          dispatch(setWeeklyTierListLoaded(true));
          return;
        }

        const deliveredPath = `/members/${user.uid}/weeklyTierLists/${tierList.slug}`;
        console.log(
          "[WeeklyTierList] subscribing to delivered doc",
          deliveredPath
        );
        const deliveredRef = doc(db, deliveredPath);

        unsubscribeDeliveredWeeklyTierList = onSnapshot(
          deliveredRef,
          (snap) => {
            const data = snap.exists()
              ? (snap.data() as DeliveredWeeklyTierList)
              : null;
            console.log("[WeeklyTierList] delivered snapshot received", {
              path: deliveredPath,
              exists: snap.exists(),
              data,
            });
            dispatch(setDeliveredWeeklyTierList(data));
            dispatch(setWeeklyTierListLoaded(true));
          },
          (err) => {
            console.log(
              "[WeeklyTierList] error on delivered listener:",
              deliveredPath,
              err
            );
            dispatch(setDeliveredWeeklyTierList(null));
            dispatch(setWeeklyTierListLoaded(true));
          }
        );
      },
      (err) => {
        console.log("[WeeklyTierList] error on latest listener:", err);
        dispatch(setLatestWeeklyTierList(null));
        dispatch(setDeliveredWeeklyTierList(null));
        dispatch(setWeeklyTierListLoaded(true));
      }
    );

    const latestWeeklyVennQuery = query(
      collection(db, "/weeklyVenns"),
      where("releasedOn", ">", 0),
      orderBy("releasedOn", "desc"),
      limit(1)
    );

    let unsubscribeDeliveredWeeklyVenn: (() => void) | null = null;

    const unsubscribeLatestWeeklyVenn = onSnapshot(
      latestWeeklyVennQuery,
      (querySnapshot) => {
        const venn = querySnapshot.docs[0]?.data() as WeeklyVenn | undefined;

        if (unsubscribeDeliveredWeeklyVenn) {
          unsubscribeDeliveredWeeklyVenn();
          unsubscribeDeliveredWeeklyVenn = null;
        }

        if (!venn) {
          dispatch(setLatestWeeklyVenn(null));
          dispatch(setDeliveredWeeklyVenn(null));
          dispatch(setWeeklyVennLoaded(true));
          return;
        }

        dispatch(setLatestWeeklyVenn(venn));

        if (!user?.uid) {
          dispatch(setDeliveredWeeklyVenn(null));
          dispatch(setWeeklyVennLoaded(true));
          return;
        }

        const deliveredPath = `/members/${user.uid}/weeklyVenns/${venn.slug}`;
        const deliveredRef = doc(db, deliveredPath);

        unsubscribeDeliveredWeeklyVenn = onSnapshot(
          deliveredRef,
          (snap) => {
            const data = snap.exists()
              ? (snap.data() as DeliveredWeeklyVenn)
              : null;
            dispatch(setDeliveredWeeklyVenn(data));
            dispatch(setWeeklyVennLoaded(true));
          },
          () => {
            dispatch(setDeliveredWeeklyVenn(null));
            dispatch(setWeeklyVennLoaded(true));
          }
        );
      },
      () => {
        dispatch(setLatestWeeklyVenn(null));
        dispatch(setDeliveredWeeklyVenn(null));
        dispatch(setWeeklyVennLoaded(true));
      }
    );

    const traitAssessmentResultRef = collection(
      db,
      `/members/${user?.uid}/traitAssessmentResults`
    );
    const threeMonthsAgoUnix =
      Math.floor(Date.now() / 1000) - 3 * 30 * 24 * 60 * 60;
    const traitAssessmentResultQuery = query(
      traitAssessmentResultRef,
      where("createdOn", ">=", threeMonthsAgoUnix)
    );

    const unsubscribeTraitAssessmentResult = onSnapshot(
      traitAssessmentResultQuery,
      (querySnapshot) => {
        const results: TraitAssessmentResult[] = [];

        querySnapshot.forEach((d) => {
          const data = d.data() as TraitAssessmentResult;
          results.push(data);
        });
        dispatch(setTraitAssessmentResults(results));
      }
    );

    const traitAssessmentResponsesRef = collection(
      db,
      `/members/${user?.uid}/traitAssessmentItemResponses`
    );

    const unsubscribeTraitAssessmentResponsesRef = onSnapshot(
      traitAssessmentResponsesRef,
      (querySnapshot) => {
        const identifiers: string[] = [];
        querySnapshot.forEach((d) => {
          const data = d.data() as {
            scheduleIdentifier: string;
          };
          identifiers.push(data.scheduleIdentifier);
        });
        dispatch(setScheduleIdentitfiers(identifiers));
      }
    );

    function updateSigninDate() {
      if (user?.uid) {
        const ref = doc(db, "members", user.uid);
        updateDoc(ref, { lastSignInDate: Date.now() }).then((res) => {});
      }
    }

    function updateIsAndroid() {
      if (window.Android && user?.uid) {
        const ref = doc(db, "publicProfiles", user.uid);
        updateDoc(ref, { isAndroid: true }).then((res) => {});
      }
    }

    function updateDiemnsionSummaries() {
      const fetchSummaries = callFirebaseFunctions("fetchDimensionSummaries");
      fetchSummaries({ uid: user?.uid })
        .then((res) => {
          //@ts-ignore
          const result = res.data.dimensionSummaries as DimensionSummary[];
          const hasLongFormArray = Object.keys(
            shortFormToLongFormDimensionSlugMap
          );
          const mappedResult = result.map((summary) => {
            if (hasLongFormArray.includes(summary.dimensionSlug)) {
              const longFormSlug =
                shortFormToLongFormDimensionSlugMap[summary.dimensionSlug];
              const longFormSummary = result.find(
                (item) => item.dimensionSlug === longFormSlug
              );

              if (longFormSummary && longFormSummary.summaryViews.length) {
                return {
                  ...summary,
                  summaryViews: longFormSummary.summaryViews,
                  deltaViews: longFormSummary.deltaViews,
                };
              }
            }
            return summary;
          });
          dispatch(setDimensionSummaries(mappedResult));
        })
        .catch((err) => {
          console.log(err);
        });
    }

    function retrieveChatbotConversations() {
      getAllCurrentChatbotConversations(user?.uid).then((res) => {
        const sortedRes = res.sort(
          (a, b) =>
            (b.latestSystemMessageTime || 0) - (a.latestSystemMessageTime || 0)
        );
        dispatch(
          setCurrentConversation(sortedRes.length ? sortedRes[0] : null)
        );
        dispatch(setConversations(sortedRes));
      });
    }

    if (user?.uid) {
      ////do all stale populating here
      getIsPremium();
      updateSigninDate();
      updateIsAndroid();
      updateDiemnsionSummaries();
      handleConnections(user.uid).then((res) => {
        dispatch(setConnectionPublicProfiles(res as FriendsPublicProfile[]));
      });
      getAllStories().then((res) => {
        dispatch(setDiscoverableStories(res));
      });
      getDownloadURL(
        ref(
          firebaseStorage,
          `gs://${process.env.NEXT_PUBLIC_STORAGEBUCKET}/profileImages/${user.uid}/cropped.jpg`
        )
      )
        .then((url) => {
          dispatch(setImageURL(url));
        })
        .catch((err) => {
          console.log(err);
          getDefaultProfilePhoto(user.uid).then((value) => {
            dispatch(setImageURL(value));
          });
        });
      retrieveChatbotConversations();
    }

    return () => {
      if (user && user.uid) {
        unsubscribeAppInfo;
        unsubscribeMemberProfile;
        unsubscribePublicProfile;
        unsubscribeDimensionEvents;
        unsubscribeScales;
        unsubscribePatterns;
        unsubscribeArchetypes;
        unsubscribeDiscoveredStories;
        unsubscribeDiscoverableAnalysis;
        unsubscribeDiscoverableCompatiblitySNippets;
        unsubscribeRecievedFriendRequestDoc;
        unsubscribeSentFriendRequestDoc;
        unsubscribeAccessMapp;
        unsubscribeNutshellRequests;
        unsubscribeSnippetsAllRequests;
        unsubscribeLatestSnippetsManualLlmRequest;
        unsubscribeLoveReportLlmData;
        unsubscribeLoveReportLlmRequest;
        unsubscribeCareerReportLlmData;
        unsubscribeCareerReportLlmRequest;
        unsubscribeTherapistReportLlmRequest;
        unsubscribeTherapistReportLlmData;
        unsubscribeInitialFeed;
        unsubscribeLatestResults;
        unsubscribePeerAssessments;
        unsubscribeDailyStory;
        unsubscribeAggregatePeerScore;
        unsubscribeMessaging;
        unsubscribeAvailableQuiz;
        unsubscribePeriodicQuizResults;
        unsubscribeInsightDelivery;
        // unsubscribeDailyPolls;
        // unsubscribePollResponse;
        unsubscribeSharedPosts;
        // unsubscribeCommunityQuestions;
        unsubscribeReadOnlyProfile;
        unsubscribeScheduledAssessmentsRef;
        unsubscribeTraitAssessmentResult;
        unsubscribeTraitAssessmentResponsesRef;
        unsubscribeLatestWeeklyTwoByTwo();
        unsubscribeDeliveredWeeklyTwoByTwo?.();
        unsubscribeLatestWeeklyTierList();
        unsubscribeDeliveredWeeklyTierList?.();
        unsubscribeLatestWeeklyVenn();
        unsubscribeDeliveredWeeklyVenn?.();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    const snippetsRef = collection(
      db,
      "members",
      `${user?.uid}`,
      "discoveredSnippets"
    );

    const unsubscribeSnippets = onSnapshot(
      snippetsRef,
      (querySnapshot) => {
        let snippets: DiscoveredSnippet[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as DiscoveredSnippet;
          const finishedDimensions = hasCompletedRequiredDimensions(
            data.discoveryGate.requiredDimensionSlugs ?? [],
            userState.dimensionEvents
          );
          if (finishedDimensions) {
            snippets.push(data);
          }
        });
        snippets = snippets.filter((s) => {
          if (s.discoveryGate.numPeerAssessorsRequired) {
            if (userState.memberProfile?.peerAssesseeUids) {
              if (
                userState.memberProfile?.peerAssesseeUids.length <
                s.discoveryGate.numPeerAssessorsRequired
              ) {
                return false;
              } else {
                return true;
              }
            } else {
              return false;
            }
          } else {
            return true;
          }
        });
        dispatch(setSnippets(snippets));
      },
      (err) => {
        console.log("Error On Discovered Snippets Listener: ", err);
      }
    );

    const DiscoveredPersonalityAnalysisDoc = query(
      collection(db, "members", `${user?.uid}`, "discoveredPersonalityAnalyses")
    );

    const unsubscribeDiscoveredPersonalAnalysis = onSnapshot(
      DiscoveredPersonalityAnalysisDoc,
      (querySnapshot) => {
        let analysises: DiscoveredPersonalityAnalysis[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as DiscoverablePersonalityAnalysis;
          const finishedDimensions = hasCompletedRequiredDimensions(
            data.discoveryGate.requiredDimensionSlugs ?? [],
            userState.dimensionEvents
          );
          if (finishedDimensions) {
            analysises.push(data);
          }
        });
        dispatch(setDiscoveredPersonalAnalysis(analysises));
      },
      (err) => {
        console.log("Error On Discovered Personality Analysis Listener: ", err);
      }
    );

    return () => {
      if (user?.uid) {
        unsubscribeSnippets;
        unsubscribeDiscoveredPersonalAnalysis;
      }
    };
  }, [user?.uid, userState.dimensionEvents]);

  useEffect(() => {
    const friendUidList = userState.friendUidList;
    const closeFriendUidList = userState.closeFriendUidList;
    const deepFriendUidList = userState.deepFriendUidList;
    const peerAssessmentEvents = peerAssessmentState.events;

    const sortedRecommendationList = handlePeerAssessmentRecommendationSort(
      friendUidList,
      closeFriendUidList,
      deepFriendUidList,
      peerAssessmentEvents
    );

    dispatch(setCtaRecommendationList(sortedRecommendationList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    peerAssessmentState.events,
    userState.closeFriendUidList,
    userState.deepFriendUidList,
    userState.friendUidList,
  ]);

  async function paginateFeedItems() {
    const feedQueryPagination = query(
      collection(db, `members/${user?.uid}/feed`),
      orderBy("createdOn", "desc"),
      startAfter(lastNotification ?? null),
      limit(10)
    );

    const querySnapShot = await getDocs(feedQueryPagination);
    let allFeedItems: FeedItemNew[] = [];
    querySnapShot.forEach((doc) => {
      allFeedItems.push(doc.data() as FeedItemNew);
    });
    dispatch(
      updateLastNotification(querySnapShot.docs[querySnapShot.docs.length - 1])
    );
    allFeedItems.forEach((f) => {
      dispatch(paginateFeed(f));
    });
  }

  async function paginateLatestResults() {
    const latestResultPaginateQuery = query(
      collection(db, `members/${user?.uid}/latestResults`),
      orderBy("createdOn", "desc"),
      startAfter(lastResultPulled ?? null),
      limit(20)
    );

    const querySnapShot = await getDocs(latestResultPaginateQuery);

    let allResults: LatestResult[] = [];
    querySnapShot.forEach((doc) => {
      allResults.push(doc.data() as LatestResult);
    });

    dispatch(
      updateLastResultPulled(querySnapShot.docs[querySnapShot.docs.length - 1])
    );
    allResults.forEach((r) => {
      dispatch(paginateLatestResult(r));
    });
  }

  const values = {
    user,
    auth,
    getUser,
    signInWithApple,
    signInWithGoogle,
    signOutOfAccount,
    signInUsingCustomToken,
    updateUserPhoneNumber,
    paginateFeedItems,
    paginateLatestResults,
  };

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

//hooks

export function useAuth(): AuthContextModel {
  return useContext(AuthContext);
}
